import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import type { User } from '@sendbird/chat';
import type { Member } from '@sendbird/chat/groupChannel';

import { chatParticipantsSelector } from 'domain/chat';

interface UserProfileCardProps {
  user: User | Member;
  /** Closes the Sendbird avatar dropdown that hosts this card. */
  onClose: () => void;
}

const UserProfileCard: React.FC<UserProfileCardProps> = ({ user, onClose }) => {
  const { formatMessage } = useIntl();
  const chatUsers = useSelector(chatParticipantsSelector);
  const chatUser = useMemo(
    () => chatUsers.find((u: { get: (key: string) => string }) => u.get('userGUID') === user.userId),
    [chatUsers, user.userId],
  );
  const email: string | undefined = chatUser?.get('userId');

  return (
    <ClickAwayListener onClickAway={onClose}>
      <Paper
        elevation={8}
        sx={(theme) => ({
          position: 'relative',
          width: 320,
          borderRadius: `${theme.shape.borderRadius}px`,
          backgroundColor: 'common.white',
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        })}
      >
        <IconButton
          aria-label={formatMessage({ id: 'chat.userProfile.close', defaultMessage: 'Close user profile' })}
          size="small"
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'text.secondary',
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Avatar
          src={user.profileUrl || undefined}
          sx={(theme) => ({
            width: 80,
            height: 80,
            border: `2px solid ${theme.palette.divider}`,
          })}
        >
          {user.nickname?.charAt(0)?.toUpperCase()}
        </Avatar>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography
            sx={(theme) => ({
              fontSize: theme.typography.pxToRem(18),
              fontWeight: theme.typography.fontWeightSemiBold,
              color: 'text.primary',
            })}
          >
            {user.nickname}
          </Typography>
          {email && (
            <Typography variant="body1" color="text.primary">
              {email}
            </Typography>
          )}
        </Box>
      </Paper>
    </ClickAwayListener>
  );
};

export default UserProfileCard;
